import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { AboutContent } from '@backstage/plugin-catalog';
import {
  InfoCard,
  InfoCardVariants,
} from '@backstage/core-components';
import { Grid, Chip, Typography, makeStyles } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';

const useStyles = makeStyles(theme => ({
  label: {
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: 0.5,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontWeight: 'bold',
    overflow: 'hidden',
    lineHeight: '24px',
    wordBreak: 'break-word',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  chip: {
    marginTop: theme.spacing(0.5),
  },
  tierChip: {
    marginTop: theme.spacing(0.5),
  },
  pciScope: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  pciInScope: {
    color: theme.palette.info.light, // Light blue/cyan color
  },
}));

type EntityAboutCardExtendedProps = {
  variant?: InfoCardVariants;
};

export const EntityAboutCardExtended = ({
  variant = 'gridItem',
}: EntityAboutCardExtendedProps) => {
  const classes = useStyles();
  const { entity } = useEntity();

  // Custom fields
  const tier = entity.spec?.tier as number | undefined;
  const techStack = entity.spec?.techStack as string[] | undefined;
  const locations = entity.spec?.locations as string[] | undefined;
  const disasterRecoveryStrategy = entity.spec?.disasterRecoveryStrategy as string | undefined;
  const pciScope = entity.spec?.pciScope as boolean | undefined;

  // Check if any custom fields exist
  const hasCustomFields = 
    tier !== undefined ||
    (techStack && techStack.length > 0) ||
    (locations && locations.length > 0) ||
    disasterRecoveryStrategy !== undefined ||
    pciScope !== undefined;

  // Helper functions
  const getTierColor = (tierValue: number): 'primary' | 'secondary' | 'default' => {
    if (tierValue === 1) return 'secondary';
    if (tierValue === 2) return 'primary';
    return 'default';
  };

  const getTierLabel = (tierValue: number): string => {
    return `Tier ${tierValue}`;
  };

  // Helper to render About card without action buttons
  const renderAboutCard = () => (
    <InfoCard title="About" variant={variant}>
      <AboutContent entity={entity} />
    </InfoCard>
  );

  // If no custom fields, render About card without action buttons
  if (!hasCustomFields) {
    return renderAboutCard();
  }

  return (
    <Grid container spacing={3} direction="column">
      <Grid item>
        {/* Custom About card (AboutContent wrapped in InfoCard) without action buttons */}
        {renderAboutCard()}
      </Grid>
      
      <Grid item>
        {/* Additional Properties Card */}
        <InfoCard title="Additional Properties" variant={variant}>
          <Grid container spacing={2}>
            {/* Service Tier */}
            {tier !== undefined && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" className={classes.label}>
                  Service Tier
                </Typography>
                <Chip
                  label={getTierLabel(tier)}
                  color={getTierColor(tier)}
                  size="small"
                  className={classes.tierChip}
                />
              </Grid>
            )}

            {/* Technology Stack */}
            {techStack && techStack.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.label}>
                  Technology Stack
                </Typography>
                <div className={classes.chipContainer}>
                  {techStack.map(tech => (
                    <Chip
                      key={tech}
                      label={tech}
                      size="small"
                      variant="outlined"
                      className={classes.chip}
                    />
                  ))}
                </div>
              </Grid>
            )}

            {/* Deployment Locations */}
            {locations && locations.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.label}>
                  Deployment Locations
                </Typography>
                <div className={classes.chipContainer}>
                  {locations.map(location => (
                    <Chip
                      key={location}
                      label={location}
                      size="small"
                      color="primary"
                      variant="outlined"
                      className={classes.chip}
                    />
                  ))}
                </div>
              </Grid>
            )}

            {/* Disaster Recovery Strategy */}
            {disasterRecoveryStrategy && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" className={classes.label}>
                  Disaster Recovery Strategy
                </Typography>
                <Typography variant="body2" className={classes.value}>
                  {disasterRecoveryStrategy}
                </Typography>
              </Grid>
            )}

            {/* PCI Scope */}
            {pciScope !== undefined && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" className={classes.label}>
                  PCI Compliance Scope
                </Typography>
                <div className={classes.pciScope}>
                  {pciScope ? (
                    <>
                      <CheckCircleIcon className={classes.pciInScope} fontSize="small" />
                      <Typography variant="body2" className={classes.pciInScope}>
                        In Scope
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CancelIcon color="action" fontSize="small" />
                      <Typography variant="body2">
                        Out of Scope
                      </Typography>
                    </>
                  )}
                </div>
              </Grid>
            )}
          </Grid>
        </InfoCard>
      </Grid>
    </Grid>
  );
};
